/**
 * Service-worker registration.
 *
 * Registered exactly once from `main.tsx`. `registerType: 'prompt'` (see
 * vite.config.ts) means a new version only ever activates after the user
 * accepts the in-app update notification — never mid-transaction.
 */
import { registerSW } from 'virtual:pwa-register'
import type { QueryClient } from '@tanstack/react-query'
import { updateStore } from './update-store'
import { isSafeToRefresh } from './transaction-guard'

type UpdateFn = (reloadPage?: boolean) => Promise<void>

let updateSW: UpdateFn | null = null

export function registerServiceWorker(): void {
  if (updateSW) return

  try {
    updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        updateStore.set({ updateAvailable: true, deferredByTransaction: false })
      },
      onOfflineReady() {
        updateStore.set({ offlineReady: true })
        window.setTimeout(() => updateStore.set({ offlineReady: false }), 6000)
      },
      onRegisterError(error) {
        // Not fatal: the app keeps running as a normal web app.
        console.warn('[pwa] service worker registration failed', error)
        updateStore.set({ registerFailed: true })
      },
    })
  } catch (error) {
    console.warn('[pwa] service worker unavailable', error)
    updateStore.set({ registerFailed: true })
  }
}

/**
 * Activate the waiting service worker and reload — but only when no sale,
 * payment or unsaved form is in flight. Returns false when it deferred.
 */
export async function applyUpdate(queryClient?: QueryClient): Promise<boolean> {
  if (!updateSW) return false

  if (!isSafeToRefresh(queryClient)) {
    updateStore.set({ deferredByTransaction: true })
    return false
  }

  updateStore.set({ applying: true, deferredByTransaction: false })
  try {
    await updateSW(true)
    return true
  } catch (error) {
    console.warn('[pwa] applying update failed', error)
    updateStore.set({ applying: false, registerFailed: true })
    return false
  }
}
